import React, { useMemo, useState } from 'react';
import { Card, Grid, ISelectOption, Select, SelectField, TextField } from '../../ui-toolkit';
import { getOrderGroupOptionsForTransactionCategory } from '../order/overview/Summary/summaryTab/orderType/OrderTypeSummaryItemContainer';
import { Show } from '../shared/Show';
import { FormikContextType, useFormikContext } from 'formik';
import { eOrderGroupCategory } from '../../ui-toolkit/enums/eOrderGroupCategory';
import { createDefaultMobilityData, createDefaultMobilityGroupData } from './validation/MobilitySchema';
import { clearGroupValues } from './TransactionInformationForm';
import { useListMobilityGroupsQuery } from '../../API/mobilityGroups/MobilityGroupApi';
import { toast } from 'react-toastify';
import { IMobilityGroup, IProgramTypeData, MobilityTransactionTypeData } from '../../types/mobility/IMobilityGroup';
import { SelectOption } from '../../tailwindComponents/selectTW/interfaces';
import { NewOrderSpaceWrapping } from './wrappers/NewOrderSpaceWrapper';
import { ICreateOrderRequest } from '../../ui-toolkit/orders/interfaces/createOrder/ICreateOrderRequest';

const NewOrExistingOptions: ISelectOption[] = [
  { label: 'New', value: true },
  { label: 'Existing', value: false },
];

const takeTitleDefaultValue = { label: 'Not Applicable', value: null };
const takeTitleOptions: ISelectOption[] = [
  { label: 'Yes', value: true },
  {
    label: 'No',
    value: false,
  },
  takeTitleDefaultValue,
];

export const resetGroupValues = (formik: FormikContextType<ICreateOrderRequest>, selected: string | undefined) => {
  const formValues = formik.values;
  if (selected === undefined) {
    // if we are choosing to clear the group type, remove all group data
    clearGroupValues(formik);
  }
  if (selected === eOrderGroupCategory.Mobility && formValues.groupCategory !== eOrderGroupCategory.Mobility) {
    // if group type changes to mobility, setup default mobility data and clear any bulk group data
    formik.setFieldValue('mobility', createDefaultMobilityData());
    formik.setFieldValue('mobilityGroup', createDefaultMobilityGroupData());
    formik.setFieldValue('relocationEmployerName', undefined);
    formik.setFieldValue('groupId', undefined);
  } else if (selected === eOrderGroupCategory.Bulk && formValues.groupCategory !== eOrderGroupCategory.Bulk) {
    // if group type changes to bulk, clear any mobility group data
    if ('mobility' in formValues) {
      delete formValues.mobility;
    }
    if ('mobilityGroup' in formValues) {
      delete formValues.mobilityGroup;
    }
  }
};

export const buildMobilityGroupsOptions = (
  mobilityGroups: IMobilityGroup[] | undefined,
  isFetchSuccess: boolean,
  isFetchLoading: boolean,
  isMobility: boolean
) => {
  if (mobilityGroups && isFetchSuccess && isMobility) {
    return mobilityGroups.map((group) => ({
      value: group.id,
      label: `${group.programType ? group.programType.label + ' - ' : ''} ${group.externalId}`,
    }));
  }

  if (isFetchLoading) {
    return [
      {
        value: '',
        label: 'Loading...',
        isDisabled: true,
      },
    ];
  }
  return [];
};

export interface IGroupInformationFormProps {
  transactionTypes: MobilityTransactionTypeData[];
  programTypes: IProgramTypeData[];
}

export const GroupInformationForm: React.FC<IGroupInformationFormProps> = ({ transactionTypes, programTypes }) => {
  const [isNew, setIsNew] = useState<ISelectOption | null>(null);
  const formik = useFormikContext<ICreateOrderRequest>();
  const isMobility = formik.values['groupCategory'] === eOrderGroupCategory.Mobility;
  const isBulk = formik.values['groupCategory'] === eOrderGroupCategory.Bulk;
  const programTypeOptions: ISelectOption[] = [
    ...programTypes.map((pType) => ({
      label: pType.label,
      value: pType.name,
    })),
    {
      label: 'Not Applicable',
      value: null,
    },
  ];

  const defaultProgramTypeOption = programTypeOptions.find((option) => option.value == null);
  const {
    data: mobilityGroups,
    isSuccess: mobilityGroupsIsSuccess,
    isError: mobilityGroupsIsError,
    isLoading: mobilityGroupsIsLoading,
  } = useListMobilityGroupsQuery(formik.values.clientCompanyId, {
    skip: !isMobility || !formik.values.clientCompanyId,
  });

  const mobilityGroupsOptions: ISelectOption[] = useMemo(() => {
    return buildMobilityGroupsOptions(mobilityGroups, mobilityGroupsIsSuccess, mobilityGroupsIsLoading, isMobility);
  }, [mobilityGroups, mobilityGroupsIsSuccess, mobilityGroupsIsLoading, isMobility]);

  const transactionTypeOptions: SelectOption[] = transactionTypes.map((tType) => ({
    label: tType.label,
    value: tType.id,
  }));

  if (mobilityGroupsIsError) {
    toast('Error fetching mobility groups.');
    return <></>;
  }

  return (
    <NewOrderSpaceWrapping>
      <Card title={'Order Group'}>
        <Grid columnCount={5}>
          <SelectField
            name={'groupCategory'}
            label={`Order Type`}
            placeholder={`Select`}
            data-test-id={`order_group_category`}
            options={getOrderGroupOptionsForTransactionCategory(formik.values.transactionCategory)}
            onChange={(selected: string | undefined) => {
              resetGroupValues(formik, selected);
            }}
            isClearable
          />
          <Show when={isMobility}>
            <SelectField
              name={'mobility.transactionType'}
              label={'Mobility Transaction Type'}
              options={transactionTypeOptions}
            />
          </Show>
          <Show when={isBulk}>
            <TextField name={'groupId'} label={'Order Group ID'} />
          </Show>
          <Show when={isMobility} fallback={<div />}>
            <TextField
              name={'relocationEmployerName'}
              label={'Relocation Employer Name'}
              placeholder={'Employer Name'}
            />
          </Show>
          <Show when={isMobility}>
            <Select
              label={'New or Existing'}
              data-test-id={'new-or-existing-group-toggle'}
              options={NewOrExistingOptions}
              value={isNew}
              onChange={(x: any) => {
                setIsNew(x);
              }}
            />
          </Show>
          <Show when={isNew?.value === false && isMobility}>
            <SelectField name={'mobilityGroup.id'} label={'Select a Group'} options={mobilityGroupsOptions} />
          </Show>
          <Show when={isNew?.value && isMobility}>
            <div />
            <TextField name={'mobilityGroup.externalId'} label={'External Group ID'} />
            <SelectField
              name={'mobilityGroup.programType'}
              label={'Program Type'}
              options={programTypeOptions}
              defaultValue={defaultProgramTypeOption}
            />
            <SelectField
              name={'mobilityGroup.takeTitle'}
              label={'Take Title?'}
              defaultValue={takeTitleDefaultValue}
              options={takeTitleOptions}
            />
          </Show>
        </Grid>
      </Card>
    </NewOrderSpaceWrapping>
  );
};
